// Add CSS for loading overlay
const loadingOverlayCSS = `
    .fabric-drawer__loading-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        backdrop-filter: blur(2px);
    }
    
    .fabric-drawer__loading-content {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
    }
    
    .fabric-drawer__loading-overlay .lds-ring {
        width: 3.2rem;
        height: 3.2rem;
        color: #8C7C6D;
    }
    
    .fabric-drawer__loading-overlay .lds-ring span {
        width: 2.4rem;
        height: 2.4rem;
        margin: 0.4rem;
        border-width: 0.2rem;
    }
`;

// Inject CSS
if (!document.getElementById('fabric-drawer-loading-css')) {
    const style = document.createElement('style');
    style.id = 'fabric-drawer-loading-css';
    style.textContent = loadingOverlayCSS;
    document.head.appendChild(style);
}

if (!customElements.get('fabric-drawer')) {
    class FabricDrawer extends HTMLElement {
        constructor() {
            super();

            this.closeButton = this.querySelector('.fabric-drawer__close-button');
            this.overlay = this.querySelector('.fabric-drawer__overlay');
            this.container = this.querySelector('.fabric-drawer__container');
            this.form = this.querySelector('form');
            this.footerMessage = this.querySelector('.fabric-drawer__footer-message span');
            this.maxItems = 6;
            this.selectedItems = 0;

            // State management
            this.stateKey = 'fabric-drawer-unified-state';
            this.state = this.getEmptyState();

            this.form.addEventListener('submit', this.submitForm.bind(this));

            if(this.closeButton){
                this.closeButton.addEventListener('click', this.closeDrawer.bind(this));
            }

            if(this.overlay){
                this.overlay.addEventListener('click', this.closeDrawer.bind(this));
            }
            
            // Initialize checkbox states and restore samples
            this.initializeDrawer();
            
            // Listen for cart updates to refresh drawer state
            document.addEventListener('cart:updated', this.handleCartUpdate.bind(this));
            document.addEventListener('cart:refresh', this.handleCartUpdate.bind(this));
            
            // Listen for cart changes from other sources (like cart page)
            document.addEventListener('cart:change', this.handleCartUpdate.bind(this));
            document.addEventListener('cart:remove', this.handleCartUpdate.bind(this));
            
            // Listen for Shopify's native cart events
            document.addEventListener('cart.requestComplete', this.handleCartUpdate.bind(this));
            document.addEventListener('cart:requestComplete', this.handleCartUpdate.bind(this));
            
            // Listen for standard Shopify cart events
            document.addEventListener('cart:updated', this.handleCartUpdate.bind(this));
            document.addEventListener('cart:change', this.handleCartUpdate.bind(this));
            document.addEventListener('cart:remove', this.handleCartUpdate.bind(this));
            
            // Listen for cart drawer events
            document.addEventListener('cart-drawer:updated', this.handleCartUpdate.bind(this));
            
            // Also listen for URL changes to refresh when user navigates to/from cart page
            window.addEventListener('popstate', this.handleCartUpdate.bind(this));
            
            // Listen for visibility change to refresh when user returns to tab
            document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
            
            // Listen for window focus to refresh when user returns to the tab
            window.addEventListener('focus', this.handleCartUpdate.bind(this));
            
            // Listen for any AJAX requests that might affect cart
            document.addEventListener('ajaxComplete', this.handleCartUpdate.bind(this));
        }

        // ===== UNIFIED STATE MANAGEMENT =====
        
        getEmptyState() {
            return {
                selectedSamples: [], // Образцы выбранные для добавления в корзину (variant_id strings)
                cartSamples: [], // Образцы уже в корзине (variant_id strings)
                lastUpdated: Date.now()
            };
        }

        loadState() {
            try {
                const saved = localStorage.getItem(this.stateKey);
                if (saved) {
                    const state = JSON.parse(saved);
                    // Ensure all required properties exist
                    this.state = {
                        selectedSamples: state.selectedSamples || [],
                        cartSamples: state.cartSamples || [],
                        lastUpdated: state.lastUpdated || Date.now()
                    };
                } else {
                    // Try to migrate from old format
                    this.state = this.migrateOldState();
                }
            } catch (error) {
                console.warn('Error loading fabric drawer state:', error);
                this.state = this.getEmptyState();
            }
            return this.state;
        }

        migrateOldState() {
            try {
                // Try to migrate from old localStorage keys
                const oldSelected = localStorage.getItem('fabric-drawer-selected-samples');
                const oldExclusions = localStorage.getItem('fabric-drawer-user-exclusions');
                
                const newState = this.getEmptyState();
                
                if (oldSelected) {
                    newState.selectedSamples = JSON.parse(oldSelected);
                }
                
                // Save in new format and clean up old keys
                if (oldSelected) {
                    this.state = newState;
                    this.saveState();
                    localStorage.removeItem('fabric-drawer-selected-samples');
                    localStorage.removeItem('fabric-drawer-user-exclusions'); // Clean up old exclusions key too
                }
                
                return newState;
                
            } catch (error) {
                console.warn('Error migrating old state:', error);
                return this.getEmptyState();
            }
        }

        saveState() {
            try {
                this.state.lastUpdated = Date.now();
                localStorage.setItem(this.stateKey, JSON.stringify(this.state));
            } catch (error) {
                console.error('Error saving fabric drawer state:', error);
            }
        }

        // State manipulation methods
        addToSelected(variantId) {
            if (!this.state) {
                this.loadState();
            }
            
            const id = variantId.toString();
            const selectedSamples = this.state.selectedSamples || [];
            const cartSamples = this.state.cartSamples || [];
            
            if (!selectedSamples.includes(id) && !cartSamples.includes(id)) {
                selectedSamples.push(id);
                this.state.selectedSamples = selectedSamples;
                this.saveState();
                return true;
            }
            return false;
        }

        removeFromSelected(variantId) {
            if (!this.state) {
                this.loadState();
            }
            
            const id = variantId.toString();
            const selectedSamples = this.state.selectedSamples || [];
            const index = selectedSamples.indexOf(id);
            
            if (index > -1) {
                selectedSamples.splice(index, 1);
                this.state.selectedSamples = selectedSamples;
                this.saveState();
                return true;
            }
            return false;
        }

        async updateCartSamples() {
            try {
                const cartResponse = await fetch('/cart.js');
                const cartData = await cartResponse.json();
                const cartFabricSamples = cartData.items
                    .filter(item => this.isFabricSample(item))
                    .map(item => item.variant_id.toString());

                this.state.cartSamples = cartFabricSamples;
                this.saveState();
                
                // Store count in attribute for other components
                this.setAttribute('data-cart-fabric-samples', cartFabricSamples.length);
                
                return cartFabricSamples.length;
            } catch (error) {
                console.error('Error updating cart samples:', error);
                return 0;
            }
        }

        getTotalSelectedCount() {
            if (!this.state) {
                this.loadState();
            }
            const cartCount = this.state.cartSamples ? this.state.cartSamples.length : 0;
            const selectedCount = this.state.selectedSamples ? this.state.selectedSamples.length : 0;
            return cartCount + selectedCount;
        }

        getAvailableSlots() {
            if (!this.state) {
                this.loadState();
            }
            const cartCount = this.state.cartSamples ? this.state.cartSamples.length : 0;
            return Math.max(0, this.maxItems - cartCount);
        }

        canAddMore() {
            return this.getTotalSelectedCount() < this.maxItems;
        }

        // ===== LOADING OVERLAY METHODS =====
        
        showLoadingOverlay() {
            let overlay = this.querySelector('.fabric-drawer__loading-overlay');
            
            if (!overlay) {
                overlay = document.createElement('div');
                overlay.className = 'fabric-drawer__loading-overlay';
                overlay.innerHTML = `
                    <div class="fabric-drawer__loading-content">
                        <div class="lds-ring">
                            <span></span>
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                    </div>
                `;
                
                // Add overlay to the container
                const container = this.querySelector('.fabric-drawer__container');
                if (container) {
                    container.style.position = 'relative';
                    container.appendChild(overlay);
                }
            }
            
            overlay.style.display = 'flex';
        }
        
        hideLoadingOverlay() {
            const overlay = this.querySelector('.fabric-drawer__loading-overlay');
            if (overlay) {
                overlay.style.display = 'none';
            }
        }

        // ===== END UNIFIED STATE MANAGEMENT =====

        async initializeDrawer() {
            try {
                
                // Load saved state
                this.loadState();
                
                // Update cart samples to get current cart state
                await this.updateCartSamples();
                
                // Clean up state - remove items from selectedSamples if they're now in cart
                this.cleanupSelectedSamples();
                
                // Sync UI with state
                this.syncUIWithState();
                
                // Update footer message (submit button updated in syncUIWithState)
                this.updateFooterMessage();
                
                
            } catch (error) {
                console.error('❌ [INIT] Error initializing drawer:', error);
                // Fallback: reset to empty state
                this.state = this.getEmptyState();
                this.saveState();
                this.syncUIWithState();
            }
        }

        cleanupSelectedSamples() {
            // Remove any selected samples that are now in cart
            const originalCount = this.state.selectedSamples.length;
            this.state.selectedSamples = this.state.selectedSamples.filter(variantId => 
                !this.state.cartSamples.includes(variantId)
            );
            
            if (this.state.selectedSamples.length !== originalCount) {
                this.saveState();
            }
        }

        syncUIWithState() {
            // Ensure state is initialized
            if (!this.state) {
                this.loadState();
            }
            
            const allCheckboxes = this.querySelectorAll('.fabric-drawer__item-checkbox');
            let uiUpdates = 0;
            
            allCheckboxes.forEach(checkbox => {
                const variantId = checkbox.value.toString();
                const isInCart = this.state.cartSamples.includes(variantId);
                const isSelected = this.state.selectedSamples.includes(variantId);
                
                // Determine checkbox state based on unified state
                const shouldBeChecked = isInCart || isSelected;
                const shouldBeDisabled = (!shouldBeChecked && !this.canAddMore());
                
                if (checkbox.checked !== shouldBeChecked) {
                    checkbox.checked = shouldBeChecked;
                    uiUpdates++;
                }
                
                if (checkbox.disabled !== shouldBeDisabled) {
                    checkbox.disabled = shouldBeDisabled;
                    uiUpdates++;
                }
            });
            
            // Update selected count for internal tracking
            this.selectedItems = this.state.selectedSamples.length;
            
            // Update submit button state
            this.updateSubmitButton();
            
            // Show warning if limit reached
            const totalSamples = this.getTotalSelectedCount();
            this.showMaxSamplesWarning(totalSamples >= this.maxItems);
            
        }

        updateFooterMessage() {
            if (this.footerMessage) {
                // Ensure state is initialized
                if (!this.state) {
                    this.loadState();
                }
                
                // Simple message: total selected samples out of max limit
                const totalSelected = this.getTotalSelectedCount();
                this.footerMessage.textContent = `you have selected ${totalSelected} of ${this.maxItems} free samples`;
                
            }
        }

        // Legacy method - now redirects to unified updateFooterMessage
        async updateFooterMessageOnly() {
            this.updateFooterMessage();
        }

        // Helper method to count selected items not in cart (now uses unified state)
        getSelectedNotInCartCount() {
            return this.state.selectedSamples.length;
        }

        updateSelectedCount(count) {
            this.selectedItems = count;
            // Don't update footer message here - let updateCheckboxStates handle it
            this.updateSubmitButton();
        }

        updateSubmitButton() {
            const submitButton = this.querySelector('.fabric-drawer__footer-button .button-1');
            
            if (submitButton) {
                // Ensure state is initialized
                if (!this.state) {
                    this.loadState();
                }
                
                // Button should be disabled if no new items are selected (using unified state)
                const selectedCount = this.state.selectedSamples ? this.state.selectedSamples.length : 0;
                const cartCount = this.state.cartSamples ? this.state.cartSamples.length : 0;
                const checkedCount = this.querySelectorAll('.fabric-drawer__item-checkbox:checked').length;
                const shouldBeDisabled = selectedCount === 0;
                
                
                if (shouldBeDisabled) {
                    submitButton.setAttribute('disabled', 'disabled');
                } else {
                    submitButton.removeAttribute('disabled');
                }
            }
        }

        async updateCheckboxStates() {
            try {
                
                // Update cart samples in state
                await this.updateCartSamples();
                
                // Clean up selected samples (remove any that are now in cart)
                this.cleanupSelectedSamples();
                
                // Sync UI with unified state
                this.syncUIWithState();
                
                // Show warning if total limit reached (cart + selected)
                const totalSamples = this.getTotalSelectedCount();
                this.showMaxSamplesWarning(totalSamples >= this.maxItems);
                
                // Update footer message
                this.updateFooterMessage();
                
                        
                
            } catch (error) {
                console.error('❌ [UPDATE] Error updating states:', error);
            }
        }

        // Legacy method - now uses unified state
        async getCartFabricSamplesCount() {
            await this.updateCartSamples();
            return this.state.cartSamples.length;
        }

        isFabricSample(item) {
            // Check if item is a fabric sample based on properties (primary method)
            if (item.properties && item.properties._fabric_sample === 'true') {
                return true;
            }
            
                         // Check by tags (if available) - fallback method
             if (item.properties && item.properties._tags) {
                 const tags = item.properties._tags.toLowerCase();
                 if (tags.includes('fabric-sample') || tags.includes('free-sample') || tags.includes('sample')) {
                     return true;
                 }
             }
             
             // Check by product type - fallback method
             if (item.product_type && item.product_type.toLowerCase().includes('fabric')) {
                 return true;
             }
             
             // Check by title - fallback method
             if (item.title && item.title.toLowerCase().includes('sample')) {
                 return true;
             }
             
             // Check by vendor - fallback method
             if (item.vendor && item.vendor.toLowerCase().includes('fabric')) {
                 return true;
             }
            
            // Check by product ID (if you have specific fabric sample IDs) - fallback method
            // const fabricSampleIds = [123456789, 987654321]; // Add your fabric sample IDs
            // if (fabricSampleIds.includes(item.product_id)) {
            //     return true;
            // }
            
            return false;
        }

        showMaxSamplesWarning(show) {
            // Ensure state is initialized
            if (!this.state) {
                this.loadState();
            }
            
            let warningElement = this.querySelector('.fabric-drawer__warning');
            let warningMessage = this.getAttribute("data-warning-message") || "You have reached the maximum of 6 free fabric samples";
            
            const totalSamples = this.getTotalSelectedCount();
            const cartCount = this.state.cartSamples ? this.state.cartSamples.length : 0;
            const selectedCount = this.state.selectedSamples ? this.state.selectedSamples.length : 0;
            
            
            if (show && !warningElement) {
                warningElement = document.createElement('div');
                warningElement.className = 'fabric-drawer__warning';
                warningElement.innerHTML = `
                    <div class="fabric-drawer__warning-content">
                        <span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M8 5.63346V8.30013M8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8C14 11.3137 11.3137 14 8 14ZM8.0332 10.3001V10.3668L7.9668 10.3665V10.3001H8.0332Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </span>
                        <span>${warningMessage}</span>
                    </div>
                `;
                
                // Insert at the top of the drawer body, before products
                const drawerBody = this.querySelector('.fabric-drawer__body');
                if (drawerBody) {
                    drawerBody.insertBefore(warningElement, drawerBody.firstChild);
                } else {
                    console.warn('❌ [WARNING] Could not find drawer body to insert warning');
                }
            } else if (!show && warningElement) {
                warningElement.remove();
            } else if (show && warningElement) {
            }
        }

        // Legacy method - now redirects to unified saveState
        saveSelectedSamples() {
            this.saveState();
        }

        // Legacy method - now redirects to unified loadState and sync
        async restoreSelectedSamples() {
            this.loadState();
            await this.updateCartSamples();
            this.cleanupSelectedSamples();
            this.syncUIWithState();
        }

        async handleCartUpdate() {
            // Refresh drawer state when cart is updated (items added/removed)
            setTimeout(async () => {
                try {
                    
                    // Update cart samples in state
                    await this.updateCartSamples();
                    
                    // Clean up selected samples (move cart items from selected to cart)
                    this.cleanupSelectedSamples();
                    
                    // Sync UI
                    this.syncUIWithState();
                    this.updateFooterMessage();
                    
                } catch (error) {
                    console.error('❌ [CART-UPDATE] Error:', error);
                }
            }, 100);
        }

        // Method to force refresh drawer state (can be called externally)
        async refreshDrawerState() {
            try {
                await this.updateCartSamples();
                this.cleanupSelectedSamples();
                this.syncUIWithState();
                this.updateFooterMessage(); 
            } catch (error) {
                console.error('❌ [REFRESH] Error refreshing state:', error);
            }
        }

        // Legacy method - now redirects to unified syncUIWithState
        async updateDisabledStatesOnly() {
            this.syncUIWithState();
        }

        handleVisibilityChange() {
            // Refresh drawer state when user returns to the tab
            if (!document.hidden && this.classList.contains('fabric-drawer--open')) {
                this.refreshDrawerState();
            }
        }

                 updateCartCountBadge() {
             // Find cart count badges with more specific selectors
             const cartCountBadges = document.querySelectorAll('.cart-count-badge, [data-cart-count], .cart-count, .cart-badge');
             
             // Also look for common Shopify cart count patterns
             const shopifyCartBadges = document.querySelectorAll('.cart-count, .cart-badge, .cart-count-badge, [data-cart-count], .header__cart-count, .cart-icon-count, .cart-count-badge');
             
             // Combine unique elements
             const allBadges = new Set([...cartCountBadges, ...shopifyCartBadges]);
             const filteredBadges = Array.from(allBadges).filter(badge => {
                 // More strict filtering to avoid false positives
                 const text = badge.textContent || '';
                 const className = badge.className || '';
                 const id = badge.id || '';
                 const tagName = badge.tagName || '';
                 
                 // Skip if it's a list item or other container elements
                 if (['LI', 'UL', 'OL', 'DIV', 'SECTION', 'HEADER', 'NAV'].includes(tagName) && !className.toLowerCase().includes('count') && !className.toLowerCase().includes('badge')) {
                     return false;
                 }
                 
                 // Check if this element looks like a cart count badge
                 return (
                     badge.hasAttribute('data-cart-count') ||
                     className.toLowerCase().includes('cart-count') ||
                     className.toLowerCase().includes('cart-badge') ||
                     className.toLowerCase().includes('count-badge') ||
                     (className.toLowerCase().includes('count') && className.toLowerCase().includes('cart')) ||
                     (id.toLowerCase().includes('cart') && id.toLowerCase().includes('count')) ||
                     (text.match(/^\d+$/) && text.length < 4 && (className.toLowerCase().includes('count') || className.toLowerCase().includes('badge')))
                 );
             });
             
             if (filteredBadges.length > 0) {
                 // Fetch current cart data to get accurate count
                 fetch('/cart.js')
                     .then(response => response.json())
                     .then(cartData => {
                         // Calculate total items manually since item_count might be 0 for free items
                         const totalItems = cartData.items ? cartData.items.reduce((total, item) => total + item.quantity, 0) : 0;
                         
                         filteredBadges.forEach(badge => {
                             // Update text content
                             badge.textContent = totalItems;
                             
                             // Update data attribute if it exists
                             if (badge.hasAttribute('data-cart-count')) {
                                 badge.setAttribute('data-cart-count', totalItems);
                             }
                             
                             // Force show badge when items are added, even if it was hidden before
                             if (totalItems > 0) {
                                 // Remove any CSS that might be hiding the badge
                                 badge.style.removeProperty('display');
                                 badge.style.removeProperty('visibility');
                                 badge.style.removeProperty('opacity');
                                 
                                 // Force show
                                 badge.style.display = 'block';
                                 badge.style.visibility = 'visible';
                                 badge.style.opacity = '1';
                                 
                                 // Remove any classes that might hide the badge
                                 badge.classList.remove('hidden', 'invisible', 'opacity-0');
                                 badge.classList.add('visible', 'opacity-100');
                             } else {
                                 // Hide badge when cart is empty
                                 badge.style.display = 'none';
                                 badge.style.visibility = 'hidden';
                                 badge.style.opacity = '0';
                             }
                         });
                     })
                     .catch(error => {
                         console.error('Error updating cart count badge:', error);
                     });
             }
         }

                                                   // Update cart page content in real-time
            updateCartPage() {
                // This method is only called when we're already on the cart page
                this.updateCartTotals();
                this.updateCartCount();
                this.updateCartItemsList();
            }

                       // Update cart items list - gentle approach to preserve styling
            async updateCartItemsList() {
                try {
                    const response = await fetch('/cart.js');
                    const cartData = await response.json();
                    
                    // Check if cart has any items (including fabric samples with price 0)
                    const hasAnyItems = cartData.items && cartData.items.length > 0;
                    
                    if (!hasAnyItems) {
                        this.showEmptyCartMessage();
                        return;
                    }
                    
                    // Instead of rebuilding the entire list, just trigger a page refresh
                    // This is the safest way to ensure cart updates without breaking styles
                    window.location.reload();
                    
                } catch (error) {
                    console.error('Error updating cart items list:', error);
                }
            }

           // Show empty cart message
           showEmptyCartMessage() {
               const cartItemsContainer = document.querySelector('.cart__items, .cart-items, [data-cart-items], .cart-items-list');
               if (cartItemsContainer) {
                   cartItemsContainer.innerHTML = `
                       <div class="cart__empty-message">
                           <p>Your cart is empty</p>
                           <a href="/collections/all" class="button">Continue shopping</a>
                       </div>
                   `;
               }
           }

                                                   // Update cart totals
            updateCartTotals() {
                const totalElements = document.querySelectorAll('.cart__total, .cart-total, [data-cart-total], .cart-total-price, .cart-total-amount, .total-price');
                
                if (totalElements.length > 0) {
                    fetch('/cart.js')
                        .then(response => response.json())
                        .then(cartData => {
                            const totalPrice = (cartData.total_price / 100).toFixed(2);
                            
                            totalElements.forEach(element => {
                                // Update text content, preserve HTML structure
                                element.textContent = `$${totalPrice}`;
                            });
                        })
                        .catch(error => {
                            console.error('Error updating cart totals:', error);
                        });
                }
            }

                                                   // Update cart count
            updateCartCount() {
                const countElements = document.querySelectorAll('.cart__count, .cart-count, [data-cart-count], .cart-item-count, .item-count');
                
                if (countElements.length > 0) {
                    fetch('/cart.js')
                        .then(response => response.json())
                        .then(cartData => {
                            // Calculate total items manually since item_count might be 0 for free items
                            const totalItemCount = cartData.items ? cartData.items.reduce((total, item) => total + item.quantity, 0) : 0;
                            
                            countElements.forEach(element => {
                                // Update text content, preserve HTML structure
                                element.textContent = totalItemCount;
                            });
                        })
                        .catch(error => {
                            console.error('Error updating cart count:', error);
                        });
                }
            }

                   // Reinitialize cart functionality
          reinitializeCartFunctionality() {
              // This method is now simplified to avoid breaking cart page functionality
              // The cart page will handle its own reinitialization through standard Shopify events
              console.log('Cart functionality reinitialization skipped to preserve page functionality');
          }



        closeDrawer(){
            // Always hide loading overlay when closing
            this.hideLoadingOverlay();
            
            setTimeout(()=>{
                this.classList.remove('fabric-drawer--open')
                document.body.classList.remove('drawer-open');
            }, 300)

            this.container.classList.remove('open');
        }
        openDrawer(){
            // Ensure no loading overlay is visible when opening
            this.hideLoadingOverlay();
            
            this.classList.add('fabric-drawer--open');
            document.body.classList.add('drawer-open');

            setTimeout(()=>{
                this.container.classList.add('open');
                // Refresh drawer state only after animation is complete
                setTimeout(() => {
                    this.refreshDrawerState();
                }, 100);
            }, 200)
        }
                async submitForm(event){
            event.preventDefault();
            
            // Use unified state - only submit selected samples (not cart samples)
            if (this.state.selectedSamples.length === 0) {
                this.closeDrawer();
                return;
            }
            
            // Check if we would exceed limit
            const totalAfterAdding = this.state.cartSamples.length + this.state.selectedSamples.length;
            if (totalAfterAdding > this.maxItems) {
                this.showMaxSamplesWarning(true);
                return;
            }

            // Show loading overlay during cart operations
            this.showLoadingOverlay();
            
            try {
                // Create JSON data with items to add (from unified state)
                const items = [];
                this.state.selectedSamples.forEach((variantId) => {
                    const itemData = {
                        id: parseInt(variantId),
                        quantity: 1,
                        properties: {
                            '_fabric_sample': 'true',
                            '_source': 'fabric-drawer'
                        }
                    };
                    items.push(itemData);
                });

                const requestData = { items };
                // Use standard Shopify cart addition logic
                const cartItems = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
                
                if (cartItems && typeof cartItems.updateQuantity === 'function') {
                    // Add items one by one using standard logic
                    for (const itemData of items) {
                        // For adding new items, we need to use a different approach
                        // since updateQuantity is for existing items
                        const response = await fetch('/cart/add.js', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({ items: [itemData] }),
                        });
                        
                        if (!response.ok) {
                            const errorText = await response.text();
                            throw new Error(`Failed to add item to cart: ${response.status} - ${errorText}`);
                        }
                    }
                    
                    // Clear selected samples from unified state after successful update
                    this.state.selectedSamples = [];
                    this.saveState();
                    
                    // Trigger cart update event
                    document.dispatchEvent(new CustomEvent('cart:updated'));
                    
                    // Update cart count badge
                    this.updateCartCountBadge();
                    
                    // Update cart page only if we're on cart page
                    if (window.location.pathname === '/cart') {
                        this.updateCartPage();
                    }
                    
                    // Wait for all updates to complete, then redirect to cart page
                    setTimeout(() => {
                        this.hideLoadingOverlay();
                        window.location.href = '/cart';
                    }, 300); // Reduced delay for better responsiveness
                } else {
                    // Fallback to direct API call
                    const response = await fetch('/cart/add.js', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(requestData),
                    });
                    
                    if (response.ok) {
                        const data = await response.json();
                        
                        // Clear selected samples from unified state after successful update
                        this.state.selectedSamples = [];
                        this.saveState();
                        
                        // Trigger cart update event
                        document.dispatchEvent(new CustomEvent('cart:updated'));
                        
                        // Update cart count badge
                        this.updateCartCountBadge();
                        
                        // Update cart page only if we're on cart page
                        if (window.location.pathname === '/cart') {
                            this.updateCartPage();
                        }
                        
                        // Wait for all updates to complete, then redirect to cart page
                        setTimeout(() => {
                            this.hideLoadingOverlay();
                            window.location.href = '/cart';
                        }, 300); // Reduced delay for better responsiveness
                    } else {
                        const errorText = await response.text();
                        console.error('Response error:', errorText);
                        throw new Error(`Failed to add items to cart: ${response.status} - ${errorText}`);
                    }
                }
                
            } catch (error) {
                console.error('Error updating cart:', error);
                this.hideLoadingOverlay(); // Hide overlay on error
                alert('Failed to update cart. Please try again.');
            }
        }
    }

    customElements.define('fabric-drawer', FabricDrawer);
}

if (!customElements.get('fabric-drawer-button')) {
    class FabricDrawerButton extends HTMLElement {
        constructor() {
            super();

            this.fabricDrawer = document.querySelector('fabric-drawer');

            if(this.fabricDrawer){
                this.addEventListener("click", async () => {
                    this.fabricDrawer.openDrawer();
                });
            }

        }
    }

    customElements.define('fabric-drawer-button', FabricDrawerButton);
}

if (!customElements.get('fabric-drawer-item')) {
    class FabricDrawerItem extends HTMLElement {
        constructor() {
            super();
            this.checkbox = this.querySelector('.fabric-drawer__item-checkbox');
            this.removeButton = this.querySelector('.fabric-drawer__item-content-remove-button');
            this.productTitle = this.querySelector('.fabric-drawer__item-content-title');
            this.fabricDrawer = document.querySelector('fabric-drawer');
            
            if(this.checkbox){
                this.checkbox.addEventListener('change', this.handleCheckboxChange.bind(this));
            }
            
            if(this.removeButton){
                this.removeButton.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.deSelectItem();
                });
            }
            
            if(this.productTitle){
                this.productTitle.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.toggleCheckbox();
                });
            }

            // Add click handler for the entire item
            this.addEventListener('click', (e) => {
                // Don't trigger if clicking on checkbox or remove button
                if (e.target.closest('.fabric-drawer__item-checkbox') || 
                    e.target.closest('.fabric-drawer__item-content-remove-button')) {
                    return;
                }
                
                e.preventDefault();
                e.stopPropagation();
                this.toggleCheckbox();
            });
        }

        async handleCheckboxChange(event) {
            const isChecked = event.target.checked;
            const checkbox = event.target;
            const variantId = checkbox.value.toString();
            
            // Simple debouncing - prevent rapid changes
            if (checkbox.hasAttribute('data-processing')) {
                event.preventDefault();
                return;
            }
            
            checkbox.setAttribute('data-processing', 'true');
            
                        try {
                if (!isChecked) {
                    // User unchecked - handle removal logic
                    const isInCart = this.fabricDrawer.state.cartSamples.includes(variantId);
                    
                    if (isInCart) {
                        // Show loading overlay for cart operations
                        this.fabricDrawer.showLoadingOverlay();
                        
                        try {
                            // Remove from cart
                            await this.removeItemFromCart(variantId);
                            // Update cart samples in state
                            await this.fabricDrawer.updateCartSamples();
                        } finally {
                            // Always hide overlay, even if error occurred
                            this.fabricDrawer.hideLoadingOverlay();
                        }
                    } else {
                        // Just remove from selected
                        this.fabricDrawer.removeFromSelected(variantId);
                        // Hide warning if we're now below limit
                        const totalAfterRemoval = this.fabricDrawer.getTotalSelectedCount();
                        if (totalAfterRemoval < this.fabricDrawer.maxItems) {
                            this.fabricDrawer.showMaxSamplesWarning(false);
                        }
                    }
                } else {
                    // User checked - add to selected
                    if (this.fabricDrawer.canAddMore()) {
                        // Add to selected
                        this.fabricDrawer.addToSelected(variantId);
                    } else {
                        // Can't add more - revert checkbox and show warning
                        checkbox.checked = false;
                        this.fabricDrawer.showMaxSamplesWarning(true);
                    }
                }
                
                // Sync UI with updated state
                this.fabricDrawer.syncUIWithState();
                this.fabricDrawer.updateFooterMessage();
                
                        } catch (error) {
                console.error('❌ [CHECKBOX] Error handling change:', error);
                // Hide loading overlay on error
                this.fabricDrawer.hideLoadingOverlay();
                // Revert checkbox to previous state
                checkbox.checked = !isChecked;
            } finally {
                // Remove processing flag
                setTimeout(() => {
                    checkbox.removeAttribute('data-processing');
                }, 200);
            }
        }

                                                                                                                                       async removeItemFromCart(variantId) {
               try {
                   // Small delay to ensure previous operations are complete
                   await new Promise(resolve => setTimeout(resolve, 50));
                   
                   // Always use direct API call for more reliable removal
                   const cartResponse = await fetch('/cart.js');
                   const cartData = await cartResponse.json();
                   
                   // Find the item in cart by variant_id
                   const cartItem = cartData.items.find(item => 
                       item.variant_id.toString() === variantId.toString() && 
                       this.fabricDrawer.isFabricSample(item)
                   );
                   
                   if (cartItem) {
                       // Use direct API call for reliable removal
                       const response = await fetch('/cart/change.js', {
                           method: 'POST',
                           headers: {
                               'Content-Type': 'application/json',
                           },
                           body: JSON.stringify({
                               id: cartItem.key,
                               quantity: 0
                           }),
                       });
                       
                       if (response.ok) {
                           const data = await response.json();
                           
                           // Trigger cart update event
                           document.dispatchEvent(new CustomEvent('cart:updated'));
                           
                           // Update cart count badge
                           this.fabricDrawer.updateCartCountBadge();
                           
                           // Update cart page only if we're on cart page
                           if (window.location.pathname === '/cart') {
                               this.fabricDrawer.updateCartPage();
                           }
                           
                           // Refresh drawer state to update UI
                           setTimeout(async () => {
                               await this.fabricDrawer.refreshDrawerState();
                           }, 100);
                       } else {
                           console.error('Failed to remove item from cart');
                       }
                   }
               } catch (error) {
                   console.error('Error removing item from cart:', error);
               }
           }

        getSelectedCount() {
            const checkboxes = document.querySelectorAll('.fabric-drawer__item-checkbox:checked');
            return checkboxes.length;
        }

        toggleCheckbox() {
            if (this.checkbox && !this.checkbox.disabled) {
                // Toggle the checkbox state
                this.checkbox.checked = !this.checkbox.checked;
                
                // Trigger the change event to execute the same logic as manual checkbox click
                this.checkbox.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }

        async deSelectItem(){
            const variantId = this.checkbox.value.toString();
            
            try {
                // Check if item is in cart and remove it
                if (this.fabricDrawer.state.cartSamples.includes(variantId)) {
                    // Show loading overlay for cart operations
                    this.fabricDrawer.showLoadingOverlay();
                    
                    try {
                        await this.removeItemFromCart(variantId);
                        await this.fabricDrawer.updateCartSamples();
                    } finally {
                        // Always hide overlay, even if error occurred
                        this.fabricDrawer.hideLoadingOverlay();
                    }
                } else {
                    // Just remove from selected (item remains available for future selection)
                    this.fabricDrawer.removeFromSelected(variantId);
                }
                
                // Sync UI with updated state
                this.fabricDrawer.syncUIWithState();
                this.fabricDrawer.updateFooterMessage();
                
                // Hide warning if we're now below limit after removal
                const totalAfterRemoval = this.fabricDrawer.getTotalSelectedCount();
                if (totalAfterRemoval < this.fabricDrawer.maxItems) {
                    this.fabricDrawer.showMaxSamplesWarning(false);
                }
                
            } catch (error) {
                console.error('❌ [DESELECT] Error removing item:', error);
                // Hide overlay in case of error
                this.fabricDrawer.hideLoadingOverlay();
            }
        }
    }

    customElements.define('fabric-drawer-item', FabricDrawerItem);
}

// Handle fabric samples trigger buttons
document.addEventListener('DOMContentLoaded', () => {
    const fabricSamplesTriggers = document.querySelectorAll('[data-fabric-samples-trigger]');
    const fabricDrawer = document.querySelector('fabric-drawer');
    
    if (fabricDrawer) {
        fabricSamplesTriggers.forEach(trigger => {
            trigger.addEventListener('click', async () => {
                fabricDrawer.openDrawer();
            });
        });
    }
    
    // Add function for testing tooltips
    window.testTooltips = function() {
        console.log('=== Testing tooltips ===');
        
        // Check for tippy.js availability
        console.log('Tippy.js available:', typeof tippy !== 'undefined');
        
        // Find all tooltip elements
        const tooltipElements = document.querySelectorAll('[id^="group-name__tooltip-"]');
        console.log('Found tooltip elements:', tooltipElements.length);
        
        tooltipElements.forEach((element, index) => {
            const id = element.id;
            const contentWrapper = element.querySelector('.group-information__wrapper');
            const hasContent = contentWrapper && contentWrapper.innerHTML.trim().length > 0;
            
            console.log(`Tooltip ${index + 1} (${id}):`, {
                'element found': !!element,
                'has content': hasContent,
                'content': hasContent ? contentWrapper.innerHTML.trim().substring(0, 50) + '...' : 'missing'
            });
        });
        
        console.log('=== End of testing ===');
        console.log('Instructions: Open fabric drawer and click on info icons to test tooltip functionality');
    };
    
    // Automatically run test 3 seconds after page load
    setTimeout(() => {
        if (window.location.search.includes('test-tooltips') || window.location.hash.includes('test-tooltips')) {
            window.testTooltips();
        }
    }, 3000);
});